import { BTFileSystem } from "legacyComponents/FileUploadContainer.types";

import { DocumentInstanceType } from "types/enum";

import { APIHandler, convertObjectToURL } from "utilities/apiHandler";
import { type IFileUploadUtilProps } from "utilities/document/fileUpload.utils";
import { isNullOrWhitespace } from "utilities/string/string";

export interface IFileUploadHandler {
    upload(props: IFileUploadUtilProps): Promise<BTFileSystem>;
}

function getUploadUrl(props: IFileUploadUtilProps, urlParams: string): string {
    const { isExternal, externalServiceInfo, tempFileType } = props;

    if (!isExternal || !externalServiceInfo) {
        return `/api/documents/${tempFileType}/tempFile${urlParams}`;
    }

    const shouldUseShareToken =
        externalServiceInfo.shareToken &&
        externalServiceInfo.docType === DocumentInstanceType.Submittal;

    if (shouldUseShareToken) {
        return `/api/Documents/TempFileExternal?shareToken=${externalServiceInfo.shareToken!}`;
    }

    const { tempFileType: entityType, entityId, subId } = externalServiceInfo;
    return `/api/Documents/TempFileExternal?entityType=${entityType}&entityId=${entityId}&subId=${subId}`;
}

export class FileUploadHandler implements IFileUploadHandler {
    async upload(props: IFileUploadUtilProps): Promise<BTFileSystem> {
        let urlParams = convertObjectToURL({
            jobId: props.jobId,
            uploadFullResPhoto: props.uploadConfig.shouldUploadFullResolutionPhoto,
            builderId: props.builderId,
        });
        if (!isNullOrWhitespace(urlParams)) {
            urlParams = `?${urlParams}`;
        }

        const url = getUploadUrl(props, urlParams);

        const fileFormData = new FormData();
        fileFormData.append("file", props.file, props.file.name);
        fileFormData.append("name", props.file.name);

        let progress = 0;
        const total = 100;
        if (!props.uploadConfig.shouldSuppressProgressEvents) {
            props.uploadProgress(
                new ProgressEvent("progress", { lengthComputable: true, loaded: progress, total }),
            );
        }

        const simulateUpload = async () => {
            if (props.uploadConfig.shouldSuppressProgressEvents) {
                return;
            }

            return await new Promise<void>((resolve) => {
                const interval = setInterval(() => {
                    progress += 10;
                    props.uploadProgress(
                        new ProgressEvent("progress", {
                            lengthComputable: true,
                            loaded: progress,
                            total,
                        }),
                    );
                    if (progress === total) {
                        resolve();
                        clearInterval(interval);
                    }
                }, 200);
            });
        };
        const response = APIHandler(url, {
            method: "POST",
            isMultiPartPostData: true,
            data: fileFormData,
            responseType: BTFileSystem,
        });

        await Promise.all([response, simulateUpload()]);

        if (!props.uploadConfig.shouldSuppressProgressEvents) {
            props.uploadProgress(
                new ProgressEvent("progress", { lengthComputable: true, loaded: progress, total }),
            );
        }

        return response;
    }
}
